import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Camera, X, Loader2, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

async function downscale(file, maxDim = 1600, quality = 0.85) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob(b => resolve(b || file), 'image/jpeg', quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

export function HarvestPhotoPicker({ photoUrl, onChange }) {
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const blob = await downscale(file);
      const fd = new FormData();
      fd.append('file', blob, 'harvest.jpg');
      const r = await axios.post(`${API}/community/upload-photo`, fd, { withCredentials: true });
      onChange(r.data.photo_url);
    } catch (err) {
      console.error(err);
      setError('Upload failed — please try again');
    } finally { setUploading(false); }
  };

  return (
    <div>
      <input ref={inputRef} type="file" accept="image/*" onChange={pick} className="hidden" data-testid="photo-file-input" />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={pick} className="hidden" data-testid="photo-camera-input" />
      {photoUrl ? (
        <div className="relative rounded-2xl overflow-hidden border border-[#1A4331]/10">
          <img src={`${BACKEND_URL}${photoUrl}`} alt="Harvest" className="w-full h-40 object-cover" data-testid="photo-preview" />
          <button type="button" onClick={() => onChange(null)} data-testid="remove-photo-button"
            className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="flex gap-2">
          <button type="button" onClick={() => cameraRef.current?.click()} disabled={uploading} data-testid="take-photo-button"
            className="flex-1 border-2 border-dashed border-[#8EAA97]/60 hover:border-[#1A4331] rounded-2xl py-4 flex items-center justify-center gap-2 text-sm font-semibold text-[#1A4331] disabled:opacity-50 transition-colors">
            {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Camera className="w-4 h-4" /> Take a photo</>}
          </button>
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="add-photo-button"
            className="flex-1 border-2 border-dashed border-[#8EAA97]/60 hover:border-[#1A4331] rounded-2xl py-4 flex items-center justify-center gap-2 text-sm font-semibold text-[#1A4331] disabled:opacity-50 transition-colors">
            <Upload className="w-4 h-4" /> Upload
          </button>
        </div>
      )}
      {error && <p className="text-xs text-[#E2725B] mt-1" data-testid="photo-upload-error">{error}</p>}
    </div>
  );
}

export function PostPhoto({ photoUrls, cropName, postId }) {
  if (!photoUrls || photoUrls.length === 0) return null;
  const u = photoUrls[0];
  const src = u.startsWith('http') ? u : `${BACKEND_URL}${u}`;
  return (
    <img src={src} alt={`${cropName || 'Harvest'} photo`} data-testid={`post-photo-${postId}`}
      loading="lazy" className="mt-3 w-full max-h-80 object-cover rounded-2xl border border-[#1A4331]/10" />
  );
}
